import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { ListButton } from "@refinedev/antd";
import { useGetIdentity } from "@refinedev/core";

import { LeftOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Flex,
  Result,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";

import { nivelDeOperadores } from "@/utilities/nivel-de-operadores";

import { fecha } from "./agent-config";
import { agentApi, AgentStatus, AgentSummary } from "./api";
import { AsistenteDrawer } from "./AsistenteDrawer";
import { modeloDelAsistente, nombreDelAsistente } from "./asistentes";
import { CredencialDelWorker } from "./CredencialDelWorker";

const { Text } = Typography;

/**
 * Los asistentes IA del store (change conversaciones-agente-ia, tareas 5.1, 5.5 y 5.11). Desde D62
 * un store tiene varios —cada usuario con `agent: admin`—, cada uno con lo suyo: la página los
 * lista y cada uno abre su drawer. Abajo, la credencial con la que entra el pod (D63). El gate es
 * el de la API: `store_operators` Viewer para ver y Admin para guardar, igual que Operadores del Store.
 */
export const StoreAgentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const storeCode = decodeURIComponent(id ?? "");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<any>();
  const [acceso, setAcceso] = useState<
    "cargando" | "ok" | "forbidden" | "error"
  >("cargando");
  const [asistentes, setAsistentes] = useState<AgentSummary[]>([]);
  const [estado, setEstado] = useState<AgentStatus | null>(null);
  const [abierto, setAbierto] = useState<number | null>(null);

  const recargar = useCallback(
    () =>
      agentApi.asistentes(storeCode).then((l) => {
        setAsistentes(l);
        return l;
      }),
    [storeCode],
  );

  useEffect(() => {
    if (!storeCode) return;
    // La lista hace de lectura de prueba: si da 403 se muestra el motivo en vez de una página rota.
    recargar()
      .then(() => setAcceso("ok"))
      .catch((e) =>
        setAcceso(e?.response?.status === 403 ? "forbidden" : "error"),
      );
    // El latido es accesorio: si falla, la lista se ve igual.
    agentApi
      .estado(storeCode)
      .then(setEstado)
      .catch(() => setEstado(null));
  }, [storeCode, recargar]);

  const editable = nivelDeOperadores(identity, storeCode) === "admin";
  const asistente = asistentes.find((a) => a.userId === abierto) ?? null;

  if (acceso === "cargando") {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Flex vertical style={{ height: "100%", minHeight: 0 }}>
      <Flex
        align="center"
        justify="space-between"
        gap={16}
        wrap="wrap"
        style={{ marginBottom: 16, flexShrink: 0 }}
      >
        <Flex align="center">
          <ListButton
            hideText
            icon={<LeftOutlined />}
            onClick={() => navigate(-1)}
          />
          <Typography.Title level={3} style={{ marginBottom: 0, marginLeft: 10 }}>
            {t("agent.title", "Asistente IA")}
          </Typography.Title>
        </Flex>
        {acceso === "ok" && estado && (
          <Flex gap={8} align="center" wrap="wrap">
            <Badge
              status={estado.online ? "success" : "default"}
              text={estado.online ? "Pod en línea" : "Pod sin conexión"}
            />
            {(estado.baseVersion || estado.lastSeenAt) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[
                  estado.baseVersion && `versión ${estado.baseVersion}`,
                  estado.lastSeenAt &&
                    `último latido ${fecha(estado.lastSeenAt)}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
            )}
          </Flex>
        )}
      </Flex>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {acceso === "ok" ? (
          <Flex vertical gap={16}>
            <Table
              size="small"
              rowKey="userId"
              dataSource={asistentes}
              pagination={false}
              scroll={{ x: true }}
              style={{ cursor: "pointer" }}
              onRow={(a) => ({ onClick: () => setAbierto(a.userId) })}
              locale={{ emptyText: "Este Store no tiene asistentes" }}
              columns={[
                {
                  title: "Asistente",
                  render: (_, a) => (
                    <Flex vertical>
                      <Text strong>{nombreDelAsistente(a)}</Text>
                      {a.email && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {a.email}
                        </Text>
                      )}
                    </Flex>
                  ),
                },
                {
                  title: "Estado",
                  render: (_, a) => (
                    <Badge
                      status={a.enabled ? "success" : "default"}
                      text={a.enabled ? "Encendido" : "Apagado"}
                    />
                  ),
                },
                { title: "Modelo", render: (_, a) => modeloDelAsistente(a) },
                {
                  title: "API key",
                  render: (_, a) =>
                    a.hasKey ? (
                      <Text code>{a.apiKeyHint ?? "••••"}</Text>
                    ) : (
                      <Tag color="warning">Sin key</Tag>
                    ),
                },
                {
                  title: "Conocimiento",
                  render: (_, a) =>
                    a.knowledgeVersion
                      ? `Versión ${a.knowledgeVersion}`
                      : "—",
                },
                { title: "Modificado", render: (_, a) => fecha(a.updatedAt) },
                {
                  title: "",
                  align: "right",
                  render: (_, a) => (
                    <Button size="small" onClick={() => setAbierto(a.userId)}>
                      {editable ? "Configurar" : "Ver"}
                    </Button>
                  ),
                },
              ]}
            />

            <CredencialDelWorker storeCode={storeCode} editable={editable} />
          </Flex>
        ) : (
          <Result
            status={acceso === "forbidden" ? "403" : "error"}
            title={
              acceso === "forbidden"
                ? "No tenés permisos para ver los asistentes de este Store"
                : "No se pudieron cargar los asistentes"
            }
            subTitle={
              acceso === "forbidden"
                ? "Pedile a un administrador del Store el permiso de Operadores del Store."
                : "Volvé a intentar en un momento."
            }
          />
        )}
      </div>

      <AsistenteDrawer
        storeCode={storeCode}
        asistente={asistente}
        asistentes={asistentes}
        editable={editable}
        onClose={() => setAbierto(null)}
        onCambio={() => void recargar().catch(() => undefined)}
      />
    </Flex>
  );
};
